import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../Utils/axiosInstance";
import { toast } from "react-hot-toast";
import { setClearCart } from "./cart.slice";

const initialStatepayment = {
  payment: [],
  success: false,
  message: "",
  loading: false,
  clientSecret: "",
  orders: [],
  razorpayOrder: null,
  paymentStatus: null,
  error: null,
  retryPaymentLoading : false
};

const handleErrors = (error, dispatch, rejectWithValue) => {
  const errorMessage = error.response?.data?.message || "An error occurred";
  return rejectWithValue(error.response?.data || { message: errorMessage });
};

export const getAllpayment = createAsyncThunk(
  "payment/getAllpayment",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/getpayment`);
      return response.data.data;
    } catch (error) {
      return handleErrors(error, dispatch, rejectWithValue);
    }
  }
);

export const getPaymentUser = createAsyncThunk(
  "payment/getPaymentUser",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/getPaymentUser`);
      return response.data.data;
    } catch (error) {
      return handleErrors(error, dispatch, rejectWithValue);
    }
  }
);

export const createpayment = createAsyncThunk(
  "payment/createpayment",
  async (data, { dispatch, rejectWithValue }) => {
    // console.log("rest", data);

    try {
      const response = await axiosInstance.post(`/create-payment`, data);
      toast(response.data.message || "Payment Successful !", {
        variant: "success",
      });
      // console.log("response", response.data);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message ||
          error.message ||
          "payment not successful");
      return handleErrors(error, dispatch, rejectWithValue);
    }
  }
);

// 1. Create order (calls backend to create Razorpay order and DB order)
export const createOrder = createAsyncThunk(
  "payment/createOrder",
  async ({ amount }, {dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/order/create", {
        amount,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || "Order creation failed"
      );
    }
  }
);

// 2. Verify payment (calls backend to verify Razorpay payment)
export const verifyPayment = createAsyncThunk(
  "payment/verifyPayment",
  async (
    {
      items,
      amount,
      paymentResponse,
      buyNow,
      orderId
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post("/order/verify", {
        items,
        amount,
        paymentResponse,
        buyNow,
        orderId
      });
      dispatch(setClearCart());
      // Show success message if payment is successful
      if (response.data && (response.data.success || response.data.order)) {
        toast(response.data.message || "Payment Successful!", {
          variant: "success",
        });
      }
      return response.data;
    } catch (err) {
      toast.error(err.response?.data?.error || "Payment verification failed");
      return rejectWithValue(
        err.response?.data?.error || "Payment verification failed"
      );
    }
  }
);

export const retryPayment = createAsyncThunk(
  "payment/retryPayment",
  async ({ orderId }, {dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/order/retry", { orderId });
      dispatch(setClearCart());

      if (response.status === 201) {
        toast(response.data.msg || "Order already paid", {
          variant: "success",
        });
        return response.data;
      }  
    } catch (err) {
      toast.error(err.response?.data?.error || "Payment verification failed");
      return rejectWithValue(
        err.response?.data?.error || "Payment verification failed"
      );
    }
  }
);

export const allorders = createAsyncThunk(
  "payment/allOrders",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/allorders");
      return response.data.orders;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || "Payment verification failed"
      );
    }
  }
);

const paymentSlice = createSlice({
  name: "payment",
  initialState: initialStatepayment,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getPayment
      .addCase(getAllpayment.pending, (state) => {
        state.loading = true;
        state.message = "Fetching users...";
      })
      .addCase(getAllpayment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "payment fetched successfully";
        state.payment = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getAllpayment.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.message = action.payload?.message || "Failed to fetch users";
      })
      // getPaymentUser
      .addCase(getPaymentUser.pending, (state) => {
        state.loading = true;
        state.message = "Fetching users...";
      })
      .addCase(getPaymentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "payment fetched successfully";
        state.payment = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getPaymentUser.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.message = action.payload?.message || "Failed to fetch users";
      })
      // create Payment
      .addCase(createpayment.pending, (state) => {
        state.loading = true;
        state.message = "Fetching users...";
      })
      .addCase(createpayment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "payment created successfully";
        state.payment.push(action.payload.payment);
        state.clientSecret = action.payload.clientSecret;
      })
      .addCase(createpayment.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.message = action.payload?.message || "Failed to fetch users";
      })

      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        // state.orders.push(action.payload.order);
        state.razorpayOrder = action.payload.razorpayOrder;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(verifyPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.loading = false;
        const existingOrderIndex = state.orders.findIndex(o => o._id === action.payload.order._id);
        if (existingOrderIndex !== -1) {
          state.orders[existingOrderIndex] = action.payload.order;
        } else {
          state.orders.push(action.payload.order);
        }
        state.paymentStatus = action.payload.success ? "success" : "failed";
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.loading = false;
        state.paymentStatus = "failed";
        state.error = action.payload;
      })

      .addCase(retryPayment.pending, (state) => {
        state.retryPaymentLoading = true;
        state.error = null;
      })

      .addCase(retryPayment.fulfilled, (state, action) => {
        state.retryPaymentLoading = false;
        state.orders = state.orders.map((v)=>v._id === action.payload.order._id ?  action.payload.order : v);
        state.razorpayOrder = action.payload.razorpayOrder;
      })
      .addCase(retryPayment.rejected, (state, action) => {
        state.retryPaymentLoading = false;
        state.error = action.payload;
      })
      .addCase(allorders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(allorders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(allorders.rejected, (state, action) => {
        state.loading = false;
        state.orders = [];
        state.error = action.payload;
      });
  },
});

export default paymentSlice.reducer;
